// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Radio from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SystemBuilderMiniSplitZone from "@plumberssupply/App/Components/SystemBuilder/SystemBuilderMiniSplitZone";
import buildMiniSplitSystems from "@plumberssupply/App/Handlers/SystemBuilder/buildMiniSplitSystems";
import getMiniSplitBrands from "@plumberssupply/App/Handlers/SystemBuilder/getMiniSplitBrands";
import getMiniSplitBtus from "@plumberssupply/App/Handlers/SystemBuilder/getMiniSplitBtus";
import getMiniSplitZones from "@plumberssupply/App/Handlers/SystemBuilder/getMiniSplitZones";
import { CustomAppState } from "@plumberssupply/App/Store/Reducers";
import { getXngCommon } from "@plumberssupply/App/Store/Selectors/CommonSelectors";
import Button from "@plumberssupply/MobiusOverrides/Button/Button";
import LoadingOverlay from "@plumberssupply/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { SystemBuilderKey } from "@plumberssupply/XDL/constants";
import { mergeWithCss } from "@plumberssupply/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

interface OwnProps {
    extendedStyles?: SystemBuilderMiniSplitSpecificationViewStyles;
}

export interface SystemBuilderMiniSplitSpecificationViewStyles {
    wrapper?: InjectableCss;
    leftSectionWrapper?: InjectableCss;
    leftSection?: InjectableCss;
    rightSectionWrapper?: InjectableCss;
    rightSection?: InjectableCss;
    select?: SelectPresentationProps;
    radioGroup?: RadioGroupProps;
    divider?: InjectableCss;
    selectOptionMessage?: InjectableCss;
    buttonWrapper?: InjectableCss;
    zoneWrapper?: InjectableCss;
    columnSeparator?: InjectableCss;
    rowSeparator?: InjectableCss;
}

const mapStateToProps = (state: CustomAppState) => ({
    systemType: state.xSystemBuilder.systemType,
    disableViewSystemsButton: state.xSystemBuilder.disableViewSystemsButton,
    miniSplitConfugurations: state.xSystemBuilder.miniSplitConfugurations,
    step: state.xSystemBuilder.step,
    miniSplitZones: state.xSystemBuilder.miniSplitConfugurations.miniSplitZones,
    isTabletScreen: getXngCommon(state).isTabletScreen,
});

const mapDispatchToProps = {
    getMiniSplitBrands,
    getMiniSplitZones,
    getMiniSplitBtus,
    buildMiniSplitSystems,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const systemBuilderMiniSplitSpecificationViewStyles: SystemBuilderMiniSplitSpecificationViewStyles = {};

const SystemBuilderMiniSplitSpecificationView: FC<Props> = ({
    extendedStyles,
    getMiniSplitBrands,
    getMiniSplitZones,
    getMiniSplitBtus,
    disableViewSystemsButton,
    miniSplitConfugurations,
    buildMiniSplitSystems,
    step,
    miniSplitZones,
    isTabletScreen,
}) => {
    const [styles] = useState(() => mergeWithCss(systemBuilderMiniSplitSpecificationViewStyles, extendedStyles));
    const [brands, setBrands] = useState<string[]>(miniSplitConfugurations.brands);
    const [zones, setZones] = useState<string[]>(miniSplitConfugurations?.numberOfAvailableZones || []);
    const [selectedBrand, setSelectedBrand] = useState(miniSplitConfugurations?.selectedBrand);
    const [selectedNumberOfZones, setSelectedNumberOfZones] = useState(miniSplitConfugurations?.selectedNumberOfZones);
    const [btusType, setBtusType] = useState(miniSplitConfugurations?.btuType);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const dispatch = useDispatch();

    useEffect(() => {
        setSelectedBrand(miniSplitConfugurations?.selectedBrand || "");
        setSelectedNumberOfZones(miniSplitConfugurations?.selectedNumberOfZones || "");
    }, [miniSplitConfugurations]);

    useEffect(() => {
        if (!brands.length) {
            // get unitary brands
            getMiniSplitBrands({
                onSuccess(result) {
                    setBrands(result?.values);

                    const newConfigs = structuredClone(miniSplitConfugurations);
                    newConfigs.brands = result.values;

                    dispatch({
                        type: "xSystemBuilder/UpdateMiniSplitConfigurationObject",
                        miniSplitConfugurations: newConfigs,
                    } as any);
                },
            });
        }
    }, []);

    const selectBrandHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        setSelectedBrand(value);
        if (!value) {
            setZones([]);
            setSelectedNumberOfZones("");
            dispatch({
                type: "xSystemBuilder/ClearMiniSplitConfigurationObject",
            } as any);
            return;
        }

        getMiniSplitZones({
            parameter: {
                brand: value,
            },
            onSuccess(result) {
                setZones(result.values);
                setSelectedNumberOfZones("");

                const newConfigs = {
                    ...miniSplitConfugurations,
                    numberOfAvailableZones: result.values,
                    selectedBrand: value,
                    miniSplitZones: {},
                };

                dispatch({
                    type: "xSystemBuilder/UpdateMiniSplitConfigurationObject",
                    miniSplitConfugurations: newConfigs,
                } as any);

                dispatch({
                    type: "xSystemBuilder/UpdateIsViewSystemsButtonDisabled",
                    disableViewSystemsButton: true,
                } as any);
            },
        });
    };

    const selectNumberOfZonesHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        setSelectedNumberOfZones(value);

        const newConfigs = structuredClone(miniSplitConfugurations);
        newConfigs.selectedNumberOfZones = value;
        newConfigs.miniSplitZones = {};

        dispatch({
            type: "xSystemBuilder/UpdateMiniSplitConfigurationObject",
            miniSplitConfugurations: newConfigs,
        } as any);

        dispatch({
            type: "xSystemBuilder/UpdateIsViewSystemsButtonDisabled",
            disableViewSystemsButton: true,
        } as any);
    };

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setBtusType(value);

        const newConfigs = structuredClone(miniSplitConfugurations);
        newConfigs.btuType = value;

        dispatch({
            type: "xSystemBuilder/UpdateMiniSplitConfigurationObject",
            miniSplitConfugurations: newConfigs,
        } as any);
    };

    const handleViewSystem = () => {
        // remove localStorage on handleViewSystem click
        localStorage.removeItem(SystemBuilderKey);

        setIsSubmitting(true);

        const updatedZones: { btus: number; unitType: string }[] = [];
        Object.values(miniSplitZones).forEach(zone => {
            updatedZones.push({
                btus: Number(zone.selectedBtus),
                unitType: zone.selectedUnitType,
            });
        });

        const miniSplitSystemData = {
            brand: selectedBrand,
            btusType,
            zones: updatedZones,
        };

        buildMiniSplitSystems({
            parameter: {
                ...miniSplitSystemData,
            },
            onComplete() {
                setIsSubmitting(false);
            },
            onSuccess(result) {
                dispatch({
                    type: "xSystemBuilder/SetSystems",
                    systems: result?.systems,
                    systemData: miniSplitSystemData,
                } as any);
                dispatch({
                    type: "xSystemBuilder/SetStep",
                    step: step + 1,
                } as any);
            },
        });
    };

    return (
        <LoadingOverlay loading={isSubmitting}>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.leftSectionWrapper}>
                    <StyledWrapper {...styles.leftSection}>
                        <Select {...styles.select} value={selectedBrand || ""} onChange={selectBrandHandler}>
                            <option value="">{translate("Select Brand")}</option>
                            {brands?.map(brand => (
                                <option key={brand} value={brand}>
                                    {brand}
                                </option>
                            ))}
                        </Select>
                        {zones?.length > 0 && (
                            <>
                                <Select
                                    {...styles.select}
                                    value={selectedNumberOfZones || ""}
                                    onChange={selectNumberOfZonesHandler}
                                >
                                    <option value="">{translate("Select Number of Zones")}</option>
                                    {zones?.map((zone: string) => (
                                        <option key={zone} value={zone}>
                                            {zone === "1"
                                                ? `${zone} ${translate("Zone")}`
                                                : `${zone} ${translate("Zones")}`}
                                        </option>
                                    ))}
                                </Select>
                                {/* might be used later */}
                                {/* <StyledWrapper {...styles.divider} />
                            <RadioGroup
                                horizontal={true}
                                label={translate("BTU Type")}
                                onChangeHandler={handleRadioChange}
                                value={btusType}
                                {...styles.radioGroup}
                            >
                                <Radio value="cooling">{translate("Cooling")}</Radio>
                                <Radio value="heating">{translate("Heating")}</Radio>
                            </RadioGroup> */}
                            </>
                        )}
                    </StyledWrapper>
                </StyledWrapper>

                <StyledWrapper {...styles.rightSectionWrapper}>
                    <StyledWrapper {...styles.rightSection}>
                        {Number(selectedNumberOfZones) > 0 ? (
                            Array.from({ length: Number(selectedNumberOfZones) }, (_, i) => (
                                <React.Fragment key={i}>
                                    <StyledWrapper {...styles.zoneWrapper}>
                                        <SystemBuilderMiniSplitZone zoneNumber={i + 1} />
                                    </StyledWrapper>
                                    {(i + 1) % 3 > 0 && !isTabletScreen && (
                                        <StyledWrapper {...styles.columnSeparator} />
                                    )}
                                    {(i + 1) % 3 === 0 &&
                                        i + 1 !== Number(selectedNumberOfZones) &&
                                        !isTabletScreen && <StyledWrapper {...styles.rowSeparator} />}
                                </React.Fragment>
                            ))
                        ) : (
                            <StyledWrapper {...styles.selectOptionMessage}>
                                {siteMessage("Custom_SystemBuiler_SelectBrandToContinue")}
                            </StyledWrapper>
                        )}
                        <StyledWrapper {...styles.buttonWrapper}>
                            <Button disabled={disableViewSystemsButton || isSubmitting} onClick={handleViewSystem}>
                                {translate("View Systems")}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SystemBuilderMiniSplitSpecificationView);
